/**
 * Shifts and the cash-up, over the wire.
 *
 * Every write takes an idempotency key from the caller, for the same reason the
 * till does: these calls are replayed from the offline queue, and a key
 * generated inside this file would be new on each attempt — which turns a retry
 * of a fifty-rand pay-out into a hundred rand out of the drawer.
 *
 * Note what is *absent* from every request body: an expected figure and a
 * variance. Both are computed on the server from the sales ledger. A client that
 * could send them could close a shift that balances perfectly regardless of what
 * is actually in the drawer.
 */

import { api } from "@/api/client";

export type ShiftStatus = "OPEN" | "CLOSED" | "RECONCILED";

export type CashMovementType = "PAY_IN" | "PAY_OUT" | "DROP" | "FLOAT_ADJUSTMENT";

export interface CashMovement {
  id: string;
  movement_type: CashMovementType;
  amount: string;
  reason: string;
  by: string | null;
  created_at: string;
}

export interface ShiftTotals {
  cash_sales: string;
  card_sales: string;
  other_sales: string;
  sale_count: number;
  pay_ins: string;
  pay_outs: string;
  opening_float: string;
  /**
   * What the drawer should hold.
   *
   * **Optional on purpose.** The server withholds it from anyone without
   * `shifts.view_expected_cash`, so the field is simply absent for a cashier.
   * A cashier who can see the figure before counting is not counting — they are
   * confirming, and a shortage they could have covered never appears.
   */
  expected_cash?: string;
  /**
   * Whether anybody counted the drawer at the start.
   *
   * `false` when nobody opened the shift — the first sale opened it, with a
   * zero float. `expected_cash` is then short by whatever was really in the
   * drawer, so the variance reads as a surplus of about that much. The screen
   * says so, because an unexplained surplus is one nobody investigates.
   */
  float_was_counted?: boolean;
}

export interface Shift {
  id: string;
  status: ShiftStatus;
  business_date: string;
  opened_at: string;
  closed_at: string | null;
  opening_float: string;
  counted_cash: string | null;
  expected_cash: string | null;
  variance: string | null;
  sale_count: number;
  cashier_name: string;
  till_name: string;
  /** True when the first sale opened this shift because nobody else had. */
  opened_automatically?: boolean;
  totals?: ShiftTotals;
  cash_sales_total?: string;
  card_sales_total?: string;
  other_sales_total?: string;
  closing_note?: string;
  approved_by_name?: string | null;
  approved_at?: string | null;
  movements?: CashMovement[];
  /** True when the server replayed a previous submission rather than acting again. */
  duplicate?: boolean;
}

export const shiftsRepository = {
  /** The shift a till is on right now. `null` is an ordinary answer, not an error. */
  current: (tillId: string) =>
    api.get<{ shift: Shift | null }>(`/shifts/current/?till=${encodeURIComponent(tillId)}`),

  list: () => api.get<{ results: Shift[] } | Shift[]>("/shifts/"),

  detail: (id: string) => api.get<Shift>(`/shifts/${id}/`),

  open: (
    input: { till_id: string; shift_id: string; opening_float: string; opened_at?: string },
    idempotencyKey: string,
  ) => api.post<Shift>("/shifts/open/", input, { idempotencyKey }),

  cash: (
    shiftId: string,
    input: { movement_type: CashMovementType; amount: string; reason: string },
    idempotencyKey: string,
  ) => api.post<CashMovement>(`/shifts/${shiftId}/cash/`, input, { idempotencyKey }),

  close: (
    shiftId: string,
    input: { counted_cash: string; note?: string; closed_at?: string },
    idempotencyKey: string,
  ) => api.post<Shift>(`/shifts/${shiftId}/close/`, input, { idempotencyKey }),

  approve: (shiftId: string, note: string) =>
    api.post<Shift>(`/shifts/${shiftId}/approve/`, { note }),
};
